/**
 * How many of a session's values are still waiting to be accepted as official
 * (roadmap 1C, owner ruling 4, 2026-10-14).
 *
 * A value saved in a session is for that session only; it is also offered up
 * as a contribution (a value_proposals row with this session's id). Until
 * someone who approves values accepts it on the item page, it is "waiting".
 */

import type { ValueProposal } from '../types/prioritization/itemLifecycle';

/** Open contributions made in this session (status 'proposed'), optionally limited to some items. */
export function waitingSessionContributions(
  proposals: readonly ValueProposal[],
  sessionId: string,
  itemIds?: readonly string[],
): ValueProposal[] {
  const only = itemIds ? new Set(itemIds) : null;
  return proposals.filter(
    (p) => p.status === 'proposed' && p.sessionId === sessionId && (!only || only.has(p.itemId)),
  );
}

/** The plain line: "These values are for this session. 3 are waiting to be accepted as official." */
export function sessionValuesWaitingLine(waitingCount: number): string {
  const head = 'These values are for this session.';
  if (waitingCount <= 0) return `${head} None are waiting to be accepted as official.`;
  return `${head} ${waitingCount} ${waitingCount === 1 ? 'is' : 'are'} waiting to be accepted as official.`;
}

/** Waiting count per item, in first-seen order — the places an approver goes to accept them. */
export function waitingByItem(waiting: readonly ValueProposal[]): Array<{ itemId: string; count: number }> {
  const counts = new Map<string, number>();
  for (const p of waiting) counts.set(p.itemId, (counts.get(p.itemId) ?? 0) + 1);
  return [...counts.entries()].map(([itemId, count]) => ({ itemId, count }));
}
